import type { HistoryState } from 'vue-router'

export interface ProductListingReturn extends HistoryState {
  href: string
  text: string
}

export interface ProductSearchSurfaceReturn {
  href: string
  query: string
  scrollTop: number
  resultCount: number
  armed: boolean
}

interface ValidListingPath {
  href: string
  locale: string
  pathAndQuery: string
}

const LOCAL_BASE_URL = 'https://listing-return.invalid'
const LOCALE_RE = /^[a-z]{2}(?:-[A-Za-z]{2})?$/
const ENCODED_PATH_SEPARATOR_OR_DOT_RE = /%(?:2e|2f|5c)/i

function containsControlCharacters(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0)
    return code <= 0x1F || (code >= 0x7F && code <= 0x9F)
  })
}

function containsUnsafeCharacters(value: string): boolean {
  if (/\s/.test(value) || value.includes('\\') || containsControlCharacters(value))
    return true

  try {
    const decoded = decodeURIComponent(value)
    return decoded.includes('\\') || containsControlCharacters(decoded)
  }
  catch {
    return true
  }
}

function normalizeListingPath(value: unknown, expectedLocale?: string): ValidListingPath | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || containsUnsafeCharacters(value))
    return null

  const rawPath = value.split(/[?#]/, 1)[0]!
  if (ENCODED_PATH_SEPARATOR_OR_DOT_RE.test(rawPath))
    return null

  try {
    const base = new URL(LOCAL_BASE_URL)
    const destination = new URL(value, base)
    if (destination.origin !== base.origin)
      return null

    const localeMatch = destination.pathname.match(/^\/([^/]+)\//)
    const locale = localeMatch?.[1]
    if (!locale || !LOCALE_RE.test(locale) || (expectedLocale !== undefined && locale !== expectedLocale))
      return null

    const escapedLocale = locale.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const listingPath = new RegExp(`^/${escapedLocale}/(?:collections(?:/[^/]+)*|search|stores/[^/]+/[^/]+)/?$`)
    if (!listingPath.test(destination.pathname))
      return null

    return {
      href: `${destination.pathname}${destination.search}${destination.hash}`,
      locale,
      pathAndQuery: `${destination.pathname}${destination.search}`,
    }
  }
  catch {
    return null
  }
}

/** Parse a serialised return target, accepting only listings in the active locale. */
export function parseProductListingReturn(value: unknown, locale: string): ProductListingReturn | null {
  if (!LOCALE_RE.test(locale) || typeof value !== 'object' || value === null || Array.isArray(value))
    return null

  try {
    if (!Object.prototype.hasOwnProperty.call(value, 'href') || !Object.prototype.hasOwnProperty.call(value, 'text'))
      return null

    const { href, text } = value as Record<string, unknown>
    const listingPath = normalizeListingPath(href, locale)
    if (!listingPath || typeof text !== 'string' || containsControlCharacters(text) || !text.trim())
      return null

    return { href: listingPath.href, text: text.trim() }
  }
  catch {
    return null
  }
}

/** Compare listing pathname + query while ignoring an optional scroll anchor. */
export function isSameProductListingPath(first: string, second: string): boolean {
  const firstPath = normalizeListingPath(first)
  const secondPath = normalizeListingPath(second)

  return Boolean(
    firstPath
    && secondPath
    && firstPath.locale === secondPath.locale
    && firstPath.pathAndQuery === secondPath.pathAndQuery,
  )
}

/**
 * Validate a remembered "last visited category" URL.
 *
 * Reuses `normalizeListingPath` so a remembered path goes through exactly the
 * same hardening as a history-state return target: no protocol-relative paths,
 * no control characters, no encoded separators, and a well-formed locale
 * segment. That matcher also admits /search and /stores/*, which are listings
 * but not categories — a "continue shopping" target has to be a real
 * collection, so only the collections branch is accepted here.
 *
 * `expectedLocale` is omitted when recording (any locale the visitor is
 * browsing in is worth remembering) and supplied when reading back, so a path
 * saved before a locale switch is discarded rather than sending the customer
 * into the wrong language.
 *
 * A slug is required, not just the `collections` segment: the matcher's
 * `collections(?:/[^/]+)*` also admits a bare `/{locale}/collections`, which
 * resolves against the catch-all page, finds no Vendure collection and renders
 * a permanently empty grid.
 */
export function parseCollectionReturnPath(value: unknown, expectedLocale?: string): string | null {
  if (expectedLocale !== undefined && !LOCALE_RE.test(expectedLocale))
    return null

  const listingPath = normalizeListingPath(value, expectedLocale)
  if (!listingPath)
    return null

  const pathname = listingPath.href.split(/[?#]/, 1)[0]!
  const prefix = `/${listingPath.locale}/collections/`

  return pathname.startsWith(prefix) && pathname.length > prefix.length ? listingPath.href : null
}
